import React, { useState } from 'react';
import {
  ArrowLeft,
  Download,
  AlertTriangle,
  FileText,
  ChevronDown,
  ChevronRight,
  Brain,
  Shield,
  PenTool,
  Database,
  FileWarning,
  Layers,
  Loader2,
} from 'lucide-react';
import { QualityBadge, SeverityBadge } from '../components/shared/StatusBadge';
import { useApp } from '../context/AppContext';
import { qualityRatingDescriptions } from '../data/mock';
import { generateReport } from '../utils/generateReport';
import type { FindingCategory } from '../data/mock';

const categoryIcons: Record<FindingCategory, React.FC<{ size?: number; className?: string }>> = {
  data_mismatch: (props) => <Database {...props} />,
  signature_mismatch: (props) => <PenTool {...props} />,
  test_requirement: (props) => <FileWarning {...props} />,
  formatting: (props) => <Layers {...props} />,
  compliance_gap: (props) => <Shield {...props} />,
};

const categoryLabels: Record<FindingCategory, string> = {
  data_mismatch: 'Data Mismatch',
  signature_mismatch: 'Signature Mismatch',
  test_requirement: 'Test Requirement',
  formatting: 'Formatting',
  compliance_gap: 'Compliance Gap',
};

const categoryColors: Record<FindingCategory, { bg: string; text: string }> = {
  data_mismatch: { bg: 'bg-blue-50', text: 'text-blue-600' },
  signature_mismatch: { bg: 'bg-red-50', text: 'text-red-600' },
  test_requirement: { bg: 'bg-amber-50', text: 'text-amber-600' },
  formatting: { bg: 'bg-surface-100', text: 'text-surface-600' },
  compliance_gap: { bg: 'bg-violet-50', text: 'text-violet-600' },
};

export const AnalysisResults: React.FC = () => {
  const { documents, selectedDocumentId, setActiveView, addToast } = useApp();
  const [expandedFinding, setExpandedFinding] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const doc = documents.find(d => d.id === selectedDocumentId);

  if (!doc) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <FileText size={48} className="mx-auto mb-4 text-surface-300" />
          <h2 className="text-lg font-semibold text-surface-700 mb-1">No Document Selected</h2>
          <p className="text-sm text-surface-500 mb-4">Select a document from the documents list to view its analysis.</p>
          <button
            onClick={() => setActiveView('documents')}
            className="btn-primary"
          >
            Go to Documents
          </button>
        </div>
      </div>
    );
  }

  const findings = doc.findings || [];

  // Group findings by category
  const categoryBreakdown = findings.reduce((acc, f) => {
    acc[f.category] = (acc[f.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);


  const handleDownload = async () => {
    if (!doc || downloading) return;
    setDownloading(true);
    addToast('Generating PDF report…', 'info');
    try {
      await generateReport({
        fileName: doc.fileName,
        docName: doc.docName,
        templateVersion: doc.templateVersion,
        templateFileName: doc.templateFileName,
        uploadDate: doc.uploadDate,
        fileSize: doc.fileSize,
        qualityRating: doc.qualityRating,
        issuesDetected: doc.issuesDetected,
        analysisOverview: doc.analysisOverview,
        findings: doc.findings,
      });
      addToast('Report downloaded successfully', 'success');
    } catch (err) {
      console.error('PDF generation failed', err);
      addToast('Failed to generate report', 'error');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card p-6 animate-slide-up">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setActiveView('documents')}
            className="flex items-center gap-2 text-sm text-surface-500 hover:text-surface-700 transition-colors"
          >
            <ArrowLeft size={16} /> Back to Documents
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="btn-primary flex items-center gap-2 disabled:opacity-60"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {downloading ? 'Generating…' : 'Download Analysed Report'}
          </button>
        </div>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-surface-900 mb-3">TRACE Analysis — {doc.fileName}</h2>

            {/* Document Source Info */}
            <div className="grid grid-cols-2 gap-3 mb-2">
              {/* Analysed Document */}
              <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <FileText size={16} className="text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider mb-0.5">Analysed Document</p>
                  <p className="text-sm font-medium text-surface-800 truncate">{doc.fileName}</p>
                  <p className="text-[11px] text-surface-500 mt-0.5">
                    {doc.fileSize} · {new Date(doc.uploadDate).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Knowledge Base Template */}
              <div className="flex items-start gap-3 bg-violet-50 rounded-xl p-3">
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Layers size={16} className="text-violet-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold text-violet-500 uppercase tracking-wider mb-0.5">Knowledge Base Template</p>
                  {doc.templateFileName ? (
                    <>
                      <p className="text-sm font-medium text-surface-800 truncate">{doc.templateFileName}</p>
                      <p className="text-[11px] text-surface-500 mt-0.5">{doc.templateVersion}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-surface-600">
                        {doc.templateVersion === 'Auto-detect' ? 'None — AI General Knowledge' : doc.templateVersion}
                      </p>
                      <p className="text-[11px] text-surface-400 mt-0.5">No template file uploaded</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 text-[11px] text-surface-400 mt-1">
              <span className="font-mono text-brand-400">{doc.docName}</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-50 text-brand-500 font-medium">
                <Brain size={10} /> Claude Sonnet 4.5
              </span>
            </div>
          </div>
          {doc.qualityRating && (
            <div className="text-right ml-4">
              <p className="text-[11px] font-semibold text-surface-500 uppercase tracking-wider mb-1">Quality Rating</p>
              <QualityBadge quality={doc.qualityRating as any} />
            </div>
          )}
        </div>
      </div>

      {/* Issues Summary */}
      <div className="glass-card p-6 animate-slide-up">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
            <AlertTriangle size={20} className="text-amber-600" />
          </div>
          <div>
            <h3 className="section-title">Issues Detected: {doc.issuesDetected ?? 0}</h3>
            <p className="section-subtitle">Breakdown by category</p>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-3">
          {(Object.keys(categoryLabels) as FindingCategory[]).map(cat => {
            const count = categoryBreakdown[cat] || 0;
            const colors = categoryColors[cat];
            const Icon = categoryIcons[cat];
            return (
              <div key={cat} className={`${colors.bg} rounded-2xl p-4 text-center`}>
                <Icon size={20} className={`${colors.text} mx-auto mb-2`} />
                <p className="text-2xl font-bold text-surface-800">{count}</p>
                <p className="text-[11px] text-surface-500 mt-1">{categoryLabels[cat]}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* AI Overview */}
      {doc.analysisOverview && (
        <div className="glass-card p-6 animate-slide-up">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
              <Brain size={20} className="text-brand-400" />
            </div>
            <div>
              <h3 className="section-title">TRACE Analysis Overview</h3>
              <p className="section-subtitle">LLM Used: Claude Sonnet 4.5 (Anthropic)</p>
            </div>
          </div>
          <p className="text-sm text-surface-600 leading-relaxed bg-surface-50 rounded-xl p-4">
            {doc.analysisOverview}
          </p>
        </div>
      )}

      {/* Detailed Findings */}
      {findings.length > 0 && (
        <div className="glass-card animate-slide-up">
          <div className="px-6 pt-6 pb-4 border-b border-surface-100">
            <h3 className="section-title">Detailed Findings</h3>
            <p className="section-subtitle">Click on a finding to expand details</p>
          </div>

          <div className="divide-y divide-surface-50">
            {findings.map((finding) => {
              const isExpanded = expandedFinding === finding.id;
              const cat = finding.category as FindingCategory;
              const Icon = categoryIcons[cat] || ((props: any) => <FileText {...props} />);
              const colors = categoryColors[cat] || categoryColors.formatting;

              return (
                <div key={finding.id} className={`transition-colors ${isExpanded ? 'bg-surface-50/50' : ''}`}>
                  <button
                    onClick={() => setExpandedFinding(isExpanded ? null : finding.id)}
                    className="w-full text-left px-6 py-4 flex items-start gap-4"
                  >
                    {/* Category Icon */}
                    <div className={`w-9 h-9 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Icon size={16} className={colors.text} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-surface-800">{finding.title}</span>
                        <SeverityBadge severity={finding.severity as any} />
                      </div>
                      <p className="text-xs text-surface-500 line-clamp-1">{finding.description}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-surface-400">
                        <span>Section {finding.section}</span>
                        <span>{categoryLabels[finding.category as FindingCategory]}</span>
                      </div>
                    </div>

                    {/* Expand Arrow */}
                    <div className="mt-1 text-surface-400">
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </div>
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-6 pb-6 pl-[76px] animate-fade-in space-y-4">
                      <div>
                        <p className="text-xs font-semibold text-surface-600 mb-1.5">Description</p>
                        <p className="text-sm text-surface-600 leading-relaxed">{finding.description}</p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-surface-600 mb-1.5 flex items-center gap-1.5">
                          <Brain size={12} className="text-brand-400" /> TRACE AI Comment
                        </p>
                        <div className="bg-brand-50/50 rounded-xl p-4">
                          <p className="text-sm text-surface-700 leading-relaxed">{finding.aiComment}</p>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-surface-600 mb-1.5">Recommendation</p>
                        <div className="bg-surface-900 rounded-xl p-4">
                          <p className="text-sm text-emerald-400 font-mono leading-relaxed">{finding.recommendation}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quality Rating Explanation */}
      {doc.qualityRating && (
        <div className="glass-card p-6 animate-slide-up">
          <h3 className="section-title mb-4">Quality Rating Guide</h3>
          <div className="space-y-3">
            {(Object.entries(qualityRatingDescriptions) as [string, { label: string; description: string; color: string }][]).map(([key, rating]) => {
              const isActive = doc.qualityRating === key;
              const colorMap: Record<string, string> = {
                green: 'border-emerald-400 bg-emerald-50/50',
                amber: 'border-amber-400 bg-amber-50/50',
                red: 'border-red-400 bg-red-50/50',
              };
              const inactiveStyle = 'border-surface-200 bg-surface-50/50';

              return (
                <div
                  key={key}
                  className={`rounded-2xl border-2 p-4 transition-all ${isActive ? colorMap[rating.color] || inactiveStyle : inactiveStyle
                    }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <p className={`text-sm font-semibold ${isActive ? 'text-surface-900' : 'text-surface-600'}`}>
                      {rating.label}
                    </p>
                    {isActive && (
                      <span className="text-[10px] font-medium text-brand-400 bg-brand-50 px-2 py-0.5 rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                  <p className={`text-xs leading-relaxed ${isActive ? 'text-surface-700' : 'text-surface-500'}`}>
                    {rating.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
